import { Inject, Injectable } from '@angular/core';

import { Fixture, FixturePage, FixtureType, OfferCategory } from '@cds/betting-offer';
import { LiveHighlightsResponse } from '@cds/betting-offer/domain-specific/live';
import { TagCount, TagType } from '@cds/betting-offer/tags';
import { FixtureCountsRequest, FixtureState, LiveHighlightsRequest, OfferMapping, ScoreboardMode, SortByCriteria } from '@cds/query-objects';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { FavouritesConfig, LayoutNavigationConfig, LiveSectionConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { CountItem, ItemType, RegionItem, sportModelMethods } from '@frontend/sports/common/core/data-access/sport-model';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { GridGrouping, GridSorting } from '@frontend/sports/grid/core/feature/model';
import { TopNavigationVersion } from '@frontend/sports/navigation/core/feature/models';
import { LiveUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { sumBy, uniq } from 'lodash-es';
import { Observable, combineLatest, forkJoin, iif, map, of, switchMap } from 'rxjs';

import { StatisticsConfigService } from '../statistics/statistics-config.service';
import { TabBarItem } from '../tab-bar/tab-bar.models';
import { ILiveDispatcher, LiveDispatcherEvents } from './live-dispatcher-events';
import { UpcomingTimeIntervalService } from './upcoming-time-interval.service';

export interface BaseLiveSportsModel {
    sports: CountItem[];
    tab: LiveUrlParam;
    videoEventCount: number;
}

export interface LiveSportOverviewModel extends BaseLiveSportsModel {
    regions: CountItem[];
    hasTournaments: boolean;
    page: FixturePage | undefined;
    sorting: GridSorting;
    selectedSportId: number;
    selectedRegionId?: number;
    selectedLeagueId?: number;
    isVirtual?: boolean;
}

export interface LiveSportCountsModel {
    sport?: CountItem;
    regions: CountItem[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface LiveSportListModel extends BaseLiveSportsModel {}

export interface LiveOverviewModel extends BaseLiveSportsModel {
    highlights: LiveHighlightsResponse;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface LiveFavouritesModel extends BaseLiveSportsModel {}

export type RegionBarItem = Omit<TabBarItem, 'url'> & {
    url: string;
};

@Injectable({ providedIn: 'root' })
export class LiveEventListService {
    private readonly maxOnHighlights: number;
    private readonly maxUpcomingEventsPerSport: number;
    private readonly regionThreshold = 30;
    private readonly upcomingPageSize = 10;
    readonly skipSportNavigation: boolean = false;

    private statisticsModes: string;

    constructor(
        private bettingOfferApi: BettingOfferApi,
        private sitecore: Sitecore,
        private urlHelper: UrlHelperService,
        liveConfig: LiveSectionConfig,
        private upcomingTimeIntervalService: UpcomingTimeIntervalService,
        private favouritesConfig: FavouritesConfig,
        @Inject(DispatcherService) private dispatcher: ILiveDispatcher,
        private layoutNavConfig: LayoutNavigationConfig,
        private statisticsConfigService: StatisticsConfigService,
    ) {
        this.maxOnHighlights = liveConfig.maxLiveEventsPerSport;
        this.maxUpcomingEventsPerSport = liveConfig.maxUpcomingEventsPerSport;
        this.skipSportNavigation = this.layoutNavConfig.topNavigationVersion === TopNavigationVersion.V3;
        this.statisticsModes = this.statisticsConfigService.getStatisticsModesBySportId(undefined);
    }

    getHighlights(): Observable<LiveOverviewModel | undefined> {
        const tagCounts = this.skipSportNavigation ? of([]) : this.getTagCounts({ tags: [TagType.Sport] });

        return forkJoin([this.getLiveHighlights(this.maxOnHighlights), tagCounts]).pipe(
            map(([highlights, tagCount]) => this.mapLiveOverviewResponse(highlights, tagCount, LiveUrlParam.Highlights)),
        );
    }

    getFavourites(): Observable<LiveFavouritesModel | undefined> {
        return this.getTagCounts({ tags: [TagType.Sport] }).pipe(map((data) => this.mapLiveFavouritesOverviewResponse(data)));
    }

    getLiveStreams(): Observable<LiveOverviewModel | undefined> {
        return forkJoin([this.getFixturesWithLiveStreams(false), this.getTagCounts({ tags: [TagType.Sport] })]).pipe(
            map(([highlights, tagCount]) => this.mapLiveOverviewResponse(highlights, tagCount, LiveUrlParam.Video)),
        );
    }

    getLiveUpcomingStreams(): Observable<LiveHighlightsResponse | undefined> {
        return this.getFixturesWithLiveStreams(true);
    }

    getLiveSportsList(): Observable<LiveSportListModel> {
        return this.getTagCounts({ tags: [TagType.Sport] }).pipe(
            map((tagCounts) => {
                const sportItems = sportModelMethods.fromTag(tagCounts);
                const videoCount = this.getVideoStreamsCount(sportItems);

                return {
                    sports: sportItems,
                    videoEventCount: videoCount,
                    tab: LiveUrlParam.SportList,
                };
            }),
        );
    }

    getEsports(take: number, skip?: number, sportId?: number): Observable<LiveOverviewModel | undefined> {
        return combineLatest([
            this.getLiveEsports(take, skip, sportId),
            iif(() => !!skip, of([]), this.getTagCounts({ tags: [TagType.Sport] })),
        ]).pipe(map(([highlights, tagCount]) => this.mapLiveOverviewResponse(highlights, tagCount, LiveUrlParam.Esports)));
    }

    getSportCounts(sportId: number, withRegions: boolean = false): Observable<LiveSportCountsModel> {
        const tags = withRegions ? [TagType.Sport, TagType.Region, TagType.Tournament] : [TagType.Sport];

        return this.getTagCounts({ tags, sportId }).pipe(
            map((tagCounts) => {
                const sportItems = sportModelMethods.fromTag(tagCounts);
                const sport = sportItems.find((s) => s.id === sportId);

                return {
                    sport,
                    regions: withRegions ? this.filterRegions(sport) : [],
                };
            }),
        );
    }

    getUpcomingFixtures(params: {
        sportId?: number;
        regionId?: number;
        tournamentId?: number;
        skip?: number;
        streaming?: boolean;
        leagueId?: number;
        isVirtual?: boolean;
    }): Observable<FixturePage | undefined> {
        const interval = this.getUpcomingFixturesDatetimeInterval();

        return this.bettingOfferApi.getFixtureList({
            state: FixtureState.Upcoming,
            skip: params.skip,
            take: params.skip ? this.upcomingPageSize : this.maxUpcomingEventsPerSport,
            offerCategories: OfferCategory.Gridable,
            offerMapping: OfferMapping.Filtered,
            sortBy: SortByCriteria.StartDate,
            sportIds: this.getIdValue(params.sportId),
            regionIds: this.getIdValue(params.regionId),
            tournamentIds: this.getIdValue(params.tournamentId),
            competitionIds: params.isVirtual ? undefined : this.getIdValue(params.leagueId),
            virtualCompetitionIds: params.isVirtual ? this.getIdValue(params.leagueId) : undefined,
            ...interval,
            statisticsModes: this.statisticsConfigService.getStatisticsModesBySportId(params.sportId),
        });
    }

    getFixtures(params: {
        sportId: number;
        regionId?: number;
        tournamentId?: number;
        conferenceId?: number;
        sorting?: GridSorting;
        skip?: number;
        take?: number;
        leagueId?: number;
        isVirtual?: boolean;
    }): Observable<FixturePage | undefined> {
        return this.bettingOfferApi.getFixtureList({
            state: FixtureState.Live,
            skip: params.skip,
            take: params.take,
            offerMapping: OfferMapping.Filtered,
            offerCategories: OfferCategory.Gridable,
            sortBy: params.sorting === GridSorting.Time ? SortByCriteria.StartDate : SortByCriteria.Tags,
            sportIds: this.getIdValue(params.sportId),
            regionIds: this.getIdValue(params.regionId),
            tournamentIds: this.getIdValue(params.tournamentId),
            conferenceIds: this.getIdValue(params.conferenceId),
            competitionIds: params.isVirtual ? undefined : this.getIdValue(params.leagueId),
            virtualCompetitionIds: params.isVirtual ? this.getIdValue(params.leagueId) : undefined,
            statisticsModes: this.statisticsConfigService.getStatisticsModesBySportId(params.sportId),
        });
    }

    getLiveSports(filter: {
        sportId: number;
        regionId?: number;
        tournamentId?: number;
        sorting?: GridSorting;
        fallback?: boolean;
        take?: number;
    }): Observable<LiveSportOverviewModel> {
        const requestForSport = this.skipSportNavigation ? filter.sportId : undefined;

        return this.getTagCounts({
            tags: [TagType.Sport, TagType.Region, TagType.Tournament],
            sportId: requestForSport,
        }).pipe(
            switchMap((tagCounts) => {
                filter = { ...filter };
                const sportItems = sportModelMethods.fromTag(tagCounts);
                const sport = sportItems.find((s) => s.id === filter.sportId);

                if (sport?.children[0] && sport.counts.live > this.regionThreshold) {
                    if (filter.fallback !== false && !filter.tournamentId && !filter.regionId) {
                        if (sport.children[0].type === ItemType.Tournament) {
                            filter.tournamentId = sport.children[0].id;
                        }

                        if (sport.children[0].type === ItemType.Region) {
                            filter.regionId = sport.children[0].id;
                        }
                    }
                }

                const regions = this.filterRegions(sport);
                const videoCount = this.getVideoStreamsCount(sportItems);

                return this.getFixtures(filter).pipe(
                    map((fixturePage) => ({
                        selectedSportId: filter.sportId,
                        selectedRegionId: filter.regionId || filter.tournamentId,
                        hasTournaments: !filter.regionId && !!filter.tournamentId,
                        page: fixturePage,
                        sorting: filter.sorting || GridSorting.Competition,
                        sports: sportItems,
                        regions,
                        videoEventCount: videoCount,
                        tab: filter.sportId,
                    })),
                );
            }),
        );
    }

    getLiveLeagues(filter: {
        sportId: number;
        leagueId?: number;
        regionId?: number;
        sorting?: GridSorting;
        take?: number;
        isVirtual?: boolean;
    }): Observable<LiveSportOverviewModel> {
        return this.getFixtures(filter).pipe(
            map((fixturePage) => ({
                selectedSportId: filter.sportId,
                selectedRegionId: filter.regionId,
                hasTournaments: false,
                page: fixturePage,
                sorting: filter.sorting || GridSorting.Competition,
                sports: [],
                regions: [],
                videoEventCount: 0,
                tab: filter.sportId,
                selectedLeagueId: filter.leagueId,
                isVirtual: filter.isVirtual,
            })),
        );
    }

    getSportTabs(sports: CountItem[], videos: number, active: number): TabBarItem[] {
        const tabs: TabBarItem[] = [];
        let currentSports = [...sports];

        if (this.favouritesConfig.isLiveFavouritesEnabled) {
            tabs.push({
                id: LiveUrlParam.Favourites,
                count: 0,
                title: this.sitecore.favouritesMessages.LiveTitle,
                isHidden: true,
                url: this.urlHelper.getLiveUrl(LiveUrlParam.Favourites),
                icon: 'theme-favorite',
            });
        }

        tabs.push({
            id: LiveUrlParam.Highlights,
            title: this.sitecore.liveNavigation.Highlights,
            url: this.urlHelper.getLiveUrl(LiveUrlParam.Highlights),
            icon: 'sports-all',
            ignoreBrowserHistory: true,
        });

        if (videos && videos > 0) {
            tabs.push({
                count: videos,
                id: LiveUrlParam.Video,
                title: this.sitecore.liveNavigation.Video,
                url: this.urlHelper.getLiveUrl(LiveUrlParam.Video),
                icon: 'sports-glyph-video-i',
                ignoreBrowserHistory: true,
            });
        }

        const firstEsportIndex = currentSports.findIndex((sport) => sport.isEsport);
        const tabsLengthBeforeSports = tabs.length;
        currentSports = currentSports.filter((sport) => !sport.isEsport);

        currentSports.forEach((sport) =>
            tabs.push({
                count: sport.counts.live,
                id: sport.id,
                title: sport.name,
                url: this.urlHelper.getLiveSportUrl({ sport }),
                icon: `sports-${sport.id}`,
                ignoreBrowserHistory: true,
            }),
        );

        if (firstEsportIndex !== -1) {
            // we use sports instead of currentSports, because in currentSports the esports are already filtered
            const liveEsports = sports.filter((sport) => sport.isEsport);
            const liveEsportsCount = sumBy(liveEsports, (sport: CountItem) => sport.counts.live);
            tabs.splice(tabsLengthBeforeSports + firstEsportIndex, 0, {
                count: liveEsportsCount,
                id: LiveUrlParam.Esports,
                title: this.sitecore.eSports.ESports,
                url: this.urlHelper.getLiveUrl(LiveUrlParam.Esports),
                icon: 'theme-e-sports',
                ignoreBrowserHistory: true,
            });
        }

        tabs.push({
            id: LiveUrlParam.SportList,
            title: this.sitecore.liveNavigation.AZ,
            url: this.urlHelper.getLiveUrl(LiveUrlParam.SportList),
            icon: 'sports-all',
            ignoreBrowserHistory: true,
        });

        tabs.forEach((tab) => (tab.active = tab.id === active));

        return tabs;
    }

    getRegionTabs(regions: CountItem[], sport: CountItem, selectedRegionId?: number): RegionBarItem[] {
        const tournaments = regions && regions.length && !this.isRegion(regions[0]);

        const icon = (regionId: number) => (tournaments ? undefined : `country-icon c${regionId}`);

        const pills = regions.map((region) => ({
            active: region.id === selectedRegionId,
            icon: icon(region.id),
            id: region.id,
            title: this.isRegion(region) ? region.code : region.name,
            url: this.urlHelper.getLiveSportUrl({ sport, region }),
            ignoreBrowserHistory: true,
        }));

        if (pills.length > 1) {
            pills.unshift({
                active: selectedRegionId === undefined || selectedRegionId === -1,
                icon: icon(-1),
                id: -1,
                title: 'ALL',
                url: this.urlHelper.getLiveSportUrl({ sport, region: LiveUrlParam.Sport }),
                ignoreBrowserHistory: true,
            });
        }

        if (pills.length === 1) {
            pills[0].active = true;
        }

        return pills;
    }

    getGridGrouping(events: (Fixture | EventModel)[], sortBy?: GridSorting): GridGrouping {
        if (sortBy === GridSorting.Time) {
            const fixturesStartDate = events.map((event) => new Date(event.startDate).getDate());
            const uniqDaysCount = uniq(fixturesStartDate).length;

            return uniqDaysCount === 1 ? GridGrouping.None : GridGrouping.Date;
        }

        return GridGrouping.League;
    }

    notifyParent(model: BaseLiveSportsModel | undefined): void {
        if (model) {
            this.dispatcher.dispatch(LiveDispatcherEvents.ChildViewDataUpdate, model);
        }
    }

    private isRegion(countItem?: CountItem): countItem is RegionItem {
        return (countItem && countItem.type === ItemType.Region) || false;
    }

    private filterRegions(sportItem: CountItem | undefined): CountItem[] {
        if (sportItem === undefined) {
            return [];
        }

        return sportItem.children.filter((childItem) => childItem.type === ItemType.Region || childItem.type === ItemType.Tournament);
    }

    private getVideoStreamsCount(sportItems: CountItem[]): number {
        return sportItems.map((s) => s.counts.liveStreaming).reduce((acc, count) => acc + count, 0);
    }

    private getIdValue(entityId?: number): string | undefined {
        return !entityId || entityId < 0 ? undefined : entityId.toString();
    }

    private getLiveHighlights(maxFixturesPerSport: number): Observable<LiveHighlightsResponse | undefined> {
        return this.bettingOfferApi.getLiveHighlights(this.getLiveHighlightsRequest(maxFixturesPerSport));
    }

    private getLiveEsports(maxFixturesPerSport: number, skip?: number, sportId?: number): Observable<LiveHighlightsResponse | undefined> {
        return this.bettingOfferApi.getLiveEsports(this.getLiveHighlightsRequest(maxFixturesPerSport, skip, sportId, SortByCriteria.StartDate));
    }

    private getLiveHighlightsRequest(maxFixturesPerSport: number, skip?: number, sportId?: number, sortBy?: SortByCriteria): LiveHighlightsRequest {
        return {
            state: FixtureState.Live,
            offerMapping: OfferMapping.Filtered,
            scoreboardMode: ScoreboardMode.Slim,
            offerCategories: OfferCategory.Gridable,
            maxFixturesPerSport,
            sortBy: sortBy ? sortBy : SortByCriteria.Tags,
            statisticsModes: this.statisticsModes,
            skip,
            sportIds: sportId?.toString(),
        };
    }

    private getFixturesWithLiveStreams(isUpcoming: boolean): Observable<LiveHighlightsResponse | undefined> {
        const interval = isUpcoming ? this.getUpcomingFixturesDatetimeInterval() : {};

        const request: LiveHighlightsRequest = {
            state: isUpcoming ? FixtureState.Upcoming : FixtureState.Live,
            fixtureTypes: FixtureType.Standard,
            offerMapping: OfferMapping.Filtered,
            scoreboardMode: ScoreboardMode.Slim,
            offerCategories: OfferCategory.Gridable,
            sortBy: SortByCriteria.Tags,
            maxFixturesPerSport: undefined,
            ...interval,
            statisticsModes: this.statisticsModes,
        } as any; // TODO: Put maxFixturesPerSport as an optional property only for LiveStreamingRequest & use here LiveStreamingRequest instead of LiveHighlightsRequest

        return this.bettingOfferApi.getLiveStreams(request);
    }

    private mapLiveOverviewResponse(
        highlights: LiveHighlightsResponse | undefined,
        tagCounts: TagCount[] | undefined,
        tab: LiveUrlParam,
    ): LiveOverviewModel | undefined {
        const sports = sportModelMethods.fromTag(tagCounts);

        return (
            highlights && {
                highlights,
                tab,
                sports,
                videoEventCount: this.getVideoStreamsCount(sports),
            }
        );
    }

    private mapLiveFavouritesOverviewResponse(tagCounts: TagCount[] | undefined): LiveFavouritesModel {
        const sports = sportModelMethods.fromTag(tagCounts);

        return {
            tab: LiveUrlParam.Favourites,
            sports,
            videoEventCount: this.getVideoStreamsCount(sports),
        };
    }

    private getTagCounts(filter: { tags?: TagType[]; sportId?: number }): Observable<TagCount[] | undefined> {
        const request: FixtureCountsRequest = {
            state: FixtureState.Live,
            tagTypes: (filter.tags || []).join(','),
            sortBy: SortByCriteria.Tags,
            sportIds: this.getIdValue(filter.sportId),
            extendedTags: '',
        };

        return this.bettingOfferApi.getCounts(request);
    }

    private getUpcomingFixturesDatetimeInterval(): { from: string; to: string } {
        const upcomingInterval = this.upcomingTimeIntervalService.getInterval();

        return {
            from: upcomingInterval.fromDate,
            to: upcomingInterval.toDate,
        };
    }
}
